import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import firebase from '../config/firebase.config';
import _ from 'lodash';

const database = firebase.firestore();

/**
 * Custom hook to fetch and cache the company's screenshot-capture setting
 * from companyDatabase. Mirrors useStealthMode's cache-first + onSnapshot
 * pattern for the sibling `screenshot` boolean field.
 *
 * @param {string} [overrideCompanyId] - When set (non-empty), read the setting for this company instead of the user's active company.
 * @returns {{ screenshotsEnabled: boolean, loading: boolean }}
 */
export function useScreenshotsEnabled(overrideCompanyId) {
  const userProfile = useSelector((state) => state.auth.profile);
  const activeCompanyId = !_.isEmpty(userProfile?.activeCompany)
    ? userProfile?.activeCompany?.companyId
    : null;
  const companyId =
    typeof overrideCompanyId === 'string' && overrideCompanyId.trim() !== ''
      ? overrideCompanyId.trim()
      : activeCompanyId;

  const [screenshotsEnabled, setScreenshotsEnabled] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!companyId) {
      setScreenshotsEnabled(false);
      setLoading(false);
      return;
    }

    const cacheKey = `screenshot_${companyId}`;
    const cachedValue = localStorage.getItem(cacheKey);

    if (cachedValue !== null) {
      setScreenshotsEnabled(cachedValue === 'true');
      setLoading(false);
    }

    const companyRef = database.collection('companyDatabase').doc(companyId);

    const unsubscribe = companyRef.onSnapshot(
      (doc) => {
        const enabled = doc.exists && doc.data().screenshot === true;
        setScreenshotsEnabled(enabled);
        localStorage.setItem(cacheKey, String(enabled));
        setLoading(false);
      },
      (error) => {
        console.error('Error fetching screenshot setting:', error);
        if (cachedValue !== null) {
          setScreenshotsEnabled(cachedValue === 'true');
        } else {
          setScreenshotsEnabled(false);
        }
        setLoading(false);
      }
    );

    return () => {
      unsubscribe();
    };
  }, [companyId]);

  return { screenshotsEnabled, loading };
}
